// The flight of the energy of a roll, from the die to the row of pips. Each
// energy is one coin. `planEnergyDelivery` gives the moment each coin leaves
// the die and the moment it drops into its pip. `flightPoint` and
// `flightScale` give the place and the size of a coin at each moment. Pure.

import {
  ENERGY_COIN_GROW_PORTION,
  ENERGY_COIN_SHRINK_PORTION,
  ENERGY_COIN_START_SCALE,
  ENERGY_FLIGHT_AFTER_MS,
  ENERGY_FLIGHT_BEND,
  ENERGY_FLIGHT_EASE,
  ENERGY_FLIGHT_LAND_GAP_MS,
  ENERGY_FLIGHT_MS,
  ENERGY_FLIGHT_REST_MS,
  ENERGY_FLIGHT_STAGGER_MS,
  ENERGY_PIP_STEP_MS,
} from '../constants';

export type Point = { x: number; y: number };

type Range = readonly [number, number];

/** One coin. The times are milliseconds after the die lands. */
export type EnergyFlight = {
  leaveMs: number;
  landMs: number;
  /** How high the arc goes over the straight line, as a part of the length of that line. */
  bend: number;
};

export type EnergyDelivery = {
  /** One flight for each energy, in the order the coins land. Empty when no coin flies. */
  flights: readonly EnergyFlight[];
  /** How long the die stays on the stage after it lands. */
  holdMs: number;
};

const lerp = (from: number, to: number, share: number): number => from + (to - from) * share;

const within = ([low, high]: Range, random: () => number): number => lerp(low, high, random());

const clampShare = (value: number): number => Math.min(Math.max(value, 0), 1);

/**
 * The flights for a roll that gave `gain` energy. With `flies` false no coin
 * flies, and the hold is long enough for the pips to fill one after the other.
 */
export const planEnergyDelivery = (
  gain: number,
  flies: boolean,
  random: () => number,
): EnergyDelivery => {
  if (!flies) {
    return {
      flights: [],
      holdMs: ENERGY_FLIGHT_REST_MS + gain * ENERGY_PIP_STEP_MS + ENERGY_FLIGHT_AFTER_MS,
    };
  }
  const flights: EnergyFlight[] = [];
  let lastLandMs = Number.NEGATIVE_INFINITY;
  for (let index = 0; index < gain; index += 1) {
    const leaveMs = ENERGY_FLIGHT_REST_MS + index * ENERGY_FLIGHT_STAGGER_MS;
    const landMs = Math.max(
      leaveMs + within(ENERGY_FLIGHT_MS, random),
      lastLandMs + ENERGY_FLIGHT_LAND_GAP_MS,
    );
    flights.push({ leaveMs, landMs, bend: within(ENERGY_FLIGHT_BEND, random) });
    lastLandMs = landMs;
  }
  const end = gain > 0 ? lastLandMs : ENERGY_FLIGHT_REST_MS;
  return { flights, holdMs: end + ENERGY_FLIGHT_AFTER_MS };
};

/**
 * Where the coin is at `progress`, from 0 at the die to 1 at the pip. The path
 * is an arc that goes up the page from the straight line. The coin starts
 * slow and drops fast.
 */
export const flightPoint = (from: Point, to: Point, bend: number, progress: number): Point => {
  const share = clampShare(progress) ** ENERGY_FLIGHT_EASE;
  const lift = Math.hypot(to.x - from.x, to.y - from.y) * bend;
  // The control point of a quadratic curve is twice as far from the line as the top of the arc.
  const control = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 - lift * 2 };
  const rest = 1 - share;
  return {
    x: rest * rest * from.x + 2 * rest * share * control.x + share * share * to.x,
    y: rest * rest * from.y + 2 * rest * share * control.y + share * share * to.y,
  };
};

/**
 * The size of the coin at `progress`, where 1 is the size of the pip. It grows
 * out of the die to `airScale`, then comes down to the pip.
 */
export const flightScale = (airScale: number, progress: number): number => {
  const share = clampShare(progress);
  if (share < ENERGY_COIN_GROW_PORTION) {
    return airScale * lerp(ENERGY_COIN_START_SCALE, 1, share / ENERGY_COIN_GROW_PORTION);
  }
  const shrinkFrom = 1 - ENERGY_COIN_SHRINK_PORTION;
  if (share <= shrinkFrom) return airScale;
  return lerp(airScale, 1, (share - shrinkFrom) / ENERGY_COIN_SHRINK_PORTION);
};
